import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
import { Suspense, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { Menu, useSidebarState } from "react-admin";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useTranslation } from "react-i18next";
import { usePlugins } from "./PluginProvider";
import { contributions } from "./registry";
import { Compatibility, PluginState } from "./contracts";
var usable = function (plugin, item) {
    return Boolean(plugin &&
        plugin.state === PluginState.Enabled &&
        plugin.compatibility === Compatibility.Compatible &&
        plugin.manifest.api_major === item.apiMajor);
};
/** Menu items for enabled plugins the user may read, after Templates. */
export var PluginMenu = function () {
    var _a = usePlugins(), plugins = _a.plugins, can = _a.can;
    var open = useSidebarState()[0];
    var t = useTranslation().t;
    return (_jsx(_Fragment, { children: contributions
            .filter(function (item) {
            var plugin = plugins.find(function (candidate) { return candidate.manifest.id === item.id; });
            return usable(plugin, item) && can(plugin.manifest.read_permission);
        })
            .map(function (item) { return (_jsx(Menu.Item, { to: item.route, primaryText: open ? t(item.labelKey) : "", leftIcon: _jsx(TimelineIcon, {}) }, item.id)); }) }));
};
/** Renders a plugin's screen, or explains why it is unavailable. */
export var PluginRoute = function () {
    var id = useParams().id;
    var context = usePlugins();
    var t = useTranslation().t;
    var latest = useRef(context);
    latest.current = context;
    // A stable host keeps the plugin's state across the portal's re-renders.
    var host = useMemo(function () { return ({
        can: function (permission) { return latest.current.can(permission); },
        execute: function (operation, data) { return latest.current.execute(id !== null && id !== void 0 ? id : "", operation, data); },
    }); }, [id]);
    if (context.loading && !context.plugins.length) {
        return _jsx(CircularProgress, { "aria-label": t("plugins.loading") });
    }
    if (context.error) {
        return (_jsx(Alert, { severity: "error", action: _jsx(Button, { onClick: function () { return void context.refresh(); }, children: t("plugins.retry") }), children: context.error }));
    }
    var item = contributions.find(function (candidate) { return candidate.id === id; });
    var plugin = context.plugins.find(function (candidate) { return candidate.manifest.id === id; });
    if (!item || !plugin)
        return _jsx(Alert, { severity: "warning", children: t("plugins.unavailable") });
    var name = t(item.labelKey);
    if (plugin.compatibility !== Compatibility.Compatible ||
        plugin.manifest.api_major !== item.apiMajor) {
        return _jsx(Alert, { severity: "warning", children: t("plugins.incompatibleRoute") });
    }
    if (!usable(plugin, item))
        return _jsx(Alert, { severity: "info", children: t("plugins.disabledRoute", { name: name }) });
    if (!context.can(plugin.manifest.read_permission)) {
        return _jsx(Alert, { severity: "warning", children: t("plugins.forbidden", { name: name }) });
    }
    var Component = item.component;
    return (_jsx(Box, { sx: { p: { xs: 2, md: 3 }, minWidth: 0, width: "100%", boxSizing: "border-box" }, children: _jsx(Suspense, { fallback: _jsx(CircularProgress, { "aria-label": t("plugins.loading") }), children: _jsx(Component, { host: host }, id) }) }));
};
